import React, { FC } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { HAMASPIK_LABEL_SX } from "../../../../../assets/hamaspik/theme";
import Badge from "../../../../../assets/hamaspik/Badge";
import { formatPifDate, parsePifDate } from "../../pifDates";
import { PifFlow, PifFormValues } from "../../types";
import PifSectionCard from "./PifSectionCard";

interface PifSectionApplicantProps {
  flow: PifFlow;
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
}

const APPLICANT_NOTE: Record<PifFlow, string> = {
  default: "",
  new: "Exactly as it appears on their government ID.",
  active: "These come from the employee record. If something's wrong, the employee changes it in self-service — not here.",
  inactive: "Pulled from their old record. Confirm the spelling out loud; names and details drift over the years.",
};

const PifSectionApplicant: FC<PifSectionApplicantProps> = ({ flow, values: f, onChange }) => {
  const lockPersonal = flow === "active";
  const digits = f.q.replace(/\D/g, "");

  return (
    <PifSectionCard
      id="sec-applicant"
      title="Applicant"
      note={APPLICANT_NOTE[flow]}
      badge={lockPersonal ? <Badge size="sm">FROM RECORD</Badge> : undefined}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        <Field label="First name *">
          <TextField size="small" fullWidth value={f.firstName} disabled={lockPersonal} onChange={(e) => onChange({ firstName: e.target.value })} />
        </Field>
        <Field label="Middle name">
          <TextField size="small" fullWidth value={f.middleName} disabled={lockPersonal} onChange={(e) => onChange({ middleName: e.target.value })} />
        </Field>
        <Field label="Last name *">
          <TextField size="small" fullWidth value={f.lastName} disabled={lockPersonal} onChange={(e) => onChange({ lastName: e.target.value })} />
        </Field>
        <Field label="Known as">
          <TextField size="small" fullWidth value={f.knownAs} placeholder="Preferred name, optional" onChange={(e) => onChange({ knownAs: e.target.value })} />
        </Field>
        <Field label="Date of birth *">
          <DatePicker
            value={parsePifDate(f.dob)}
            disabled={flow !== "new"}
            onChange={(value) => onChange({ dob: formatPifDate(value) })}
            slotProps={{
              textField: { size: "small", fullWidth: true, inputProps: { readOnly: true } },
            }}
          />
        </Field>
        <Field label="Social Security Number">
          <TextField
            size="small"
            fullWidth
            disabled
            value={flow === "new" ? digits.replace(/^(\d{3})(\d{2})(\d{0,4}).*$/, "$1-$2-$3") || "—" : "•••-••-••••"}
          />
        </Field>
      </Box>
    </PifSectionCard>
  );
};

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography sx={HAMASPIK_LABEL_SX}>{label}</Typography>
    {children}
  </Box>
);

export default PifSectionApplicant;
